import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { API_URL } from '../lib/api'
import { useUTM, getStoredUTM } from '../hooks/useUTM'

export default function FreeGuide() {
  useUTM()
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setSubmitting(true)
    setError('')

    try {
      const utm = getStoredUTM()
      const res = await fetch(`${API_URL}/api/funnel/lead-magnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || undefined,
          ...utm,
          visitor_id: localStorage.getItem('fc_visitor_id') || '',
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Something went wrong')
      }

      setSubmitted(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#faf9f7',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Minimal header */}
      <div style={{
        padding: '1.5rem 2rem',
      }}>
        <Link
          to="/"
          style={{
            textDecoration: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#0f172a',
            letterSpacing: '0.02em',
          }}
        >
          Fasting <span style={{ color: '#dc2626' }}>Carnivore</span>
        </Link>
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            maxWidth: '520px',
            width: '100%',
          }}
        >
          {submitted ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '1.5rem',
                color: '#16a34a',
              }}>
                &#10003;
              </div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 500,
                color: '#0f172a',
                marginBottom: '0.75rem',
              }}>
                Check your inbox
              </h2>
              <p style={{
                color: '#555',
                lineHeight: 1.7,
                marginBottom: '2rem',
              }}>
                Your 7-Day Carnivore Reset Guide is on its way. Check your email for the download link.
              </p>
              <Link
                to="/"
                style={{
                  color: '#dc2626',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                &larr; Back to Fasting Carnivore
              </Link>
            </div>
          ) : (
            <>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: 500,
                color: '#0f172a',
                lineHeight: 1.3,
                marginBottom: '0.75rem',
              }}>
                The 7-Day Carnivore Reset Guide
              </h1>
              <p style={{
                fontSize: '1.05rem',
                color: '#555',
                lineHeight: 1.7,
                marginBottom: '2rem',
              }}>
                Everything you need to start your carnivore journey — delivered to your inbox in 60 seconds.
              </p>

              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 2.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}>
                {[
                  'What to eat (and what to avoid) for the first 7 days',
                  'How to push through the Day 3 wall when cravings peak',
                  'The exact meal framework that makes carnivore simple',
                  'Why most diets fail — and why this one sticks',
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      color: '#333',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                    }}
                  >
                    <span style={{
                      flexShrink: 0,
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#dc2626',
                      marginTop: '0.55rem',
                    }} />
                    {item}
                  </motion.li>
                ))}
              </ul>

              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.875rem',
              }}>
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  style={{
                    padding: '0.875rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '5px',
                    fontSize: '1rem',
                    background: '#fff',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    padding: '0.875rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '5px',
                    fontSize: '1rem',
                    background: '#fff',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '0.975rem 1rem',
                    background: submitting ? '#999' : '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '1rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={e => {
                    if (!submitting) (e.currentTarget.style.background = '#b91c1c')
                  }}
                  onMouseLeave={e => {
                    if (!submitting) (e.currentTarget.style.background = '#dc2626')
                  }}
                >
                  {submitting ? 'Sending...' : 'Get the Free Guide'}
                </button>
                {error && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                )}
              </form>

              <p style={{
                fontSize: '0.8rem',
                color: '#999',
                marginTop: '1.25rem',
                lineHeight: 1.5,
              }}>
                No spam. Unsubscribe anytime. Results vary. No specific outcomes are guaranteed.
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  )
}
